import {FC, useEffect, useState} from 'react'
import * as Yup from 'yup'
import {useFormik, Field} from 'formik'
import {isNotEmpty, toAbsoluteUrl} from '../../../../../../_metronic/helpers'
import {initialUser, User, Category} from '../core/_models'
import clsx from 'clsx'
import {useListView} from '../core/ListViewProvider'
import {UsersListLoading} from '../components/loading/UsersListLoading'
import {createUser, getCategories, updateUser} from '../core/_requests'
import {useQueryResponse} from '../core/QueryResponseProvider'
import AsyncSelect from 'react-select/async'
import React from 'react'
import LoadingButtonWrapper from '../../../../../components/LoadingButtonWrapper'

type Props = {
  isUserLoading: boolean
  user: User
}

const userTypes = [
  {
    label: 'Admin',
    value: 'admin',
  },
  {
    label: 'Agent',
    value: 'agent',
  },
]

const levels = [
  {
    label: 'None',
    value: '0',
  },
  {
    label: 'Level 1',
    value: '1',
  },
  {
    label: 'Level 2',
    value: '2',
  },
]

let yupValidation = {
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  name: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Name is required'),
  type: Yup.string().required('Type is required'),
  level: Yup.string().required('Level is required'),
}

const addUserSchema = Yup.object().shape({
  ...yupValidation,
  password: Yup.string().required('Password is required'),
})
const editUserSchema = Yup.object().shape(yupValidation)

function UserEditModalForm({user, isUserLoading}) {
  const {setItemIdForUpdate} = useListView()
  const {refetch} = useQueryResponse()
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)

  const [userForEdit] = useState<User>({
    ...user,
    id: user.id || initialUser.id,
    avatar: user.avatar || initialUser.avatar,
    type: user.type || initialUser.type,
    level: user.level + '' || initialUser.level,
    name: user.name || initialUser.name,
    email: user.email || initialUser.email,
    password: '',
    categories: user.categories || initialUser.categories,
  })

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible)
  }

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
    setItemIdForUpdate(undefined)
  }

  const formik = useFormik({
    initialValues: userForEdit,
    validationSchema: isNotEmpty(user.id) ? editUserSchema : addUserSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(user.id)) {
          await updateUser(values)
        } else {
          await createUser(values)
        }
        setSubmitting(true)
        cancel(true)
      } catch (ex) {
        console.error(ex)
      } finally {
      }
    },
  })

  const mapResponseToValuesAndLabels = (data) => ({
    value: data.code,
    label: data.name,
  })

  async function callApi(value) {
    const data = await getCategories()
      .then((response) => response.data.map(mapResponseToValuesAndLabels))
      .then((final) => final.filter((i) => i.label.toLowerCase().includes(value.toLowerCase())))
    return data
  }

  React.useEffect(() => {
    console.log('Load User Edit')
  })

  const onCategoryChange = (data) => {
    formik.setFieldValue(
      'categories',
      data.map((item) => ({
        code: item.value,
        name: item.label,
      }))
    )
  }

  const getCategoryValue = () => {
    return formik.values.categories?.map(mapResponseToValuesAndLabels)
  }

  return (
    <>
      <form id='kt_modal_add_user_form' className='form' onSubmit={formik.handleSubmit} noValidate>
        {/* begin::Scroll */}
        <div
          className='d-flex flex-column scroll-y me-n7 pe-7'
          id='kt_modal_add_user_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_user_header'
          data-kt-scroll-wrappers='#kt_modal_add_user_scroll'
          data-kt-scroll-offset='300px'
        >
          {/* <div className='fv-row mb-7'>
              <label className='d-block fw-bold fs-6 mb-5'>Avatar</label>
              <div
                className='image-input image-input-outline'
                data-kt-image-input='true'
                style={{ backgroundImage: `url('${blankImg}')` }}
              >
                <div
                  className='image-input-wrapper w-125px h-125px'
                  style={{ backgroundImage: `url('${userAvatarImg || blankImg}')` }}
                ></div>
  
                <input
                  type='file'
                  accept='image/jpeg, image/png, application/pdf'
                />
  
              </div>
            </div> */}
          <div className='fv-row mb-7'>
            <label className='required fs-6 mb-2'>Full Name</label>
            <input
              placeholder='Full name'
              {...formik.getFieldProps('name')}
              type='text'
              name='name'
              className={clsx(
                'form-control mb-3 mb-lg-0',
                {'is-invalid': formik.touched.name && formik.errors.name},
                {
                  'is-valid': formik.touched.name && !formik.errors.name,
                }
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || isUserLoading}
            />
            {formik.touched.name && formik.errors.name && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.name}</span>
                </div>
              </div>
            )}
          </div>
          <div className='fv-row mb-7'>
            <label className='required fs-6 mb-2'>Email</label>
            <input
              placeholder='Email'
              {...formik.getFieldProps('email')}
              className={clsx(
                'form-control mb-3 mb-lg-0',
                {'is-invalid': formik.touched.email && formik.errors.email},
                {
                  'is-valid': formik.touched.email && !formik.errors.email,
                }
              )}
              type='email'
              name='email'
              autoComplete='off'
              disabled={formik.isSubmitting || isUserLoading}
            />
            {/* end::Input */}
            {formik.touched.email && formik.errors.email && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.email}</span>
                </div>
              </div>
            )}
          </div>
          <div className='fv-row mb-7'>
            <label className='fs-6 mb-2'>
              Password{' '}
              <small className='text-danger'>
                {user.id ? 'Leave empty if do not update' : '*'}
              </small>
            </label>
            <div className='position-relative'>
              <input
                placeholder='Password'
                {...formik.getFieldProps('password')}
                type={isPasswordVisible ? 'text' : 'password'}
                name='password'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid': formik.touched.password && formik.errors.password,
                  },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting || isUserLoading}
              />

              <span
                onClick={togglePasswordVisibility}
                style={{
                  cursor: 'pointer',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  width: 30,
                }}
              >
                <i className={isPasswordVisible ? 'bi bi-eye-fill' : 'bi bi-eye-slash-fill'}></i>
              </span>
            </div>
            {formik.touched.password && formik.errors.password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.password}</span>
                </div>
              </div>
            )}
          </div>
          <div className='mb-7'>
            <label className='required fs-6 mb-5'>Role</label>
            <div>
              {userTypes.map((m, index) => (
                <div key={index} className='d-inline-flex'>
                  <div className='d-flex fv-row me-10'>
                    <div className='form-check form-check-custom form-check-solid'>
                      <input
                        className='form-check-input me-1'
                        {...formik.getFieldProps('type')}
                        name='type'
                        type='radio'
                        value={m.value}
                        id={`radio-type-${index}`}
                        checked={formik.values.type === m.value}
                        disabled={formik.isSubmitting || isUserLoading}
                      />
                      <label className='form-check-label' htmlFor={`radio-type-${index}`}>
                        <div className='text-gray-800'>{m.label}</div>
                      </label>
                    </div>
                  </div>
                  <div className='separator separator-dashed my-5'></div>
                </div>
              ))}
            </div>
          </div>
          <div className='mb-7'>
            <label className='required fs-6 mb-5'>Level</label>
            <div>
              {levels.map((m, index) => (
                <div key={index} className='d-inline-flex'>
                  <div className='d-flex fv-row me-10'>
                    <div className='form-check form-check-custom form-check-solid'>
                      <input
                        className='form-check-input me-1'
                        {...formik.getFieldProps('level')}
                        name='level'
                        type='radio'
                        value={m.value}
                        id={`radio-level-${index}`}
                        checked={formik.values.level === m.value}
                        disabled={formik.isSubmitting || isUserLoading}
                      />
                      <label className='form-check-label' htmlFor={`radio-level-${index}`}>
                        <div className='text-gray-800'>{m.label}</div>
                      </label>
                    </div>
                  </div>
                  <div className='separator separator-dashed my-5'></div>
                </div>
              ))}
            </div>
          </div>
          <div className='mb-7'>
            <label className='required fs-6 mb-5'>Categories</label>
            <AsyncSelect
              className='form-control'
              name={'categories'}
              isMulti
              cacheOptions
              loadOptions={callApi}
              onChange={onCategoryChange}
              defaultOptions
              value={getCategoryValue()}
            />
          </div>
        </div>

        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            data-kt-users-modal-action='cancel'
            disabled={formik.isSubmitting || isUserLoading}
          >
            Discard
          </button>

          <LoadingButtonWrapper isLoading={formik.isSubmitting || isUserLoading}>
          <button type='submit' className='btn btn-primary' data-kt-users-modal-action='submit'>
            <span className='indicator-label'>Submit</span>
          </button>
          </LoadingButtonWrapper>
        </div>
        {/* end::Actions */}
      </form>
      {(formik.isSubmitting || isUserLoading) && <UsersListLoading />}
    </>
  )
}

export {UserEditModalForm}
